import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface Broadcast {
  id: string;
  title: string;
  body: string;
  audience: string;
  createdAt: string;
}

/**
 * LoginBroadcastModal — Shows unread login broadcasts as a modal overlay.
 * Fetches active broadcasts on mount. User must dismiss each one.
 * Uses sessionStorage to only show once per browser session (prevents re-fire on navigation).
 */
export function LoginBroadcastModal() {
  const { user } = useAuth();
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Only show broadcasts once per session
    const sessionKey = `broadcast-shown-${user.id || user.email}`;
    if (sessionStorage.getItem(sessionKey)) return;

    apiClient<Broadcast[]>('/login-broadcasts/active')
      .then((data) => {
        if (data && data.length > 0) {
          setBroadcasts(data);
        } else {
          // No broadcasts to show — mark session so we don't keep re-fetching
          sessionStorage.setItem(sessionKey, '1');
        }
      })
      .catch(() => {
        // Silent — don't block user if broadcasts fail to load
      });
  }, [user]);

  if (broadcasts.length === 0 || currentIndex >= broadcasts.length) {
    return null;
  }

  const current = broadcasts[currentIndex];
  const firstName = user?.fullName?.split(' ')[0] || '';

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await apiClient(`/login-broadcasts/${current.id}/dismiss`, { method: 'POST' });
    } catch {
      // Still advance even if dismiss fails (server will show it again next time)
    }
    setDismissing(false);
    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
    // If all broadcasts dismissed, mark session so it doesn't re-fire
    if (nextIndex >= broadcasts.length && user) {
      const sessionKey = `broadcast-shown-${user.id || user.email}`;
      sessionStorage.setItem(sessionKey, '1');
    }
  };

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="broadcast-title">
      <div style={styles.modal}>
        <div style={styles.header}>
          <span style={styles.icon}>📢</span>
          <h2 id="broadcast-title" style={styles.title}>{current.title}</h2>
        </div>
        <div style={styles.body}>
          {firstName && <div style={styles.greeting}>Hi {firstName},</div>}
          {current.body}
          <div style={styles.closing}>Rising Together with you,<br />The HUB Team</div>
        </div>
        <div style={styles.footer}>
          {broadcasts.length > 1 && (
            <span style={styles.counter}>
              {currentIndex + 1} of {broadcasts.length}
            </span>
          )}
          <button
            style={styles.dismissBtn}
            onClick={handleDismiss}
            disabled={dismissing}
            aria-label="Dismiss broadcast"
          >
            {dismissing ? 'Dismissing...' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '1rem',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    maxWidth: '480px',
    width: '100%',
    padding: '1.5rem',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    animation: 'fadeInScale 0.2s ease-out',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  icon: {
    fontSize: '1.5rem',
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#1a2e23',
  },
  body: {
    fontSize: '0.9rem',
    lineHeight: 1.6,
    color: '#333',
    whiteSpace: 'pre-wrap' as const,
    marginBottom: '1.25rem',
    maxHeight: '300px',
    overflowY: 'auto' as const,
  },
  greeting: {
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: '#1a2e23',
  },
  closing: {
    marginTop: '1rem',
    fontWeight: 700,
    fontStyle: 'italic',
    color: '#1a6b3c',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  counter: {
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  dismissBtn: {
    padding: '0.6rem 1.5rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginLeft: 'auto',
  },
};
